import { ArrowRight, Shield, CheckCircle, TrendingUp, Flame, Droplets, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const CommodityTrading = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "What commodities can I trade?", answer: "Through MCX (Multi Commodity Exchange), you can trade Gold, Silver, Crude Oil, Natural Gas, Copper, Zinc, Aluminum, Nickel, and agricultural commodities like Cotton. Gold and Crude Oil are the most popular among our clients. We provide guidance on which commodities suit your investment strategy." },
    { question: "What is the minimum margin for commodity trading?", answer: "Margin requirements vary by commodity. Typically, you need Rs.50,000-Rs.1,00,000 as initial margin. Gold mini contracts require lower margins. We help you understand margin requirements, lot sizes, and risk management before you start trading." },
    { question: "Is commodity trading risky?", answer: "Commodity trading involves higher risk due to leverage and price volatility. However, it's also an excellent portfolio diversification tool. We recommend commodity allocation of 5-10% of your portfolio. Our experts provide research-based calls with strict stop-losses to manage risk." },
    { question: "What are the trading hours for MCX?", answer: "MCX trading hours are Monday to Friday, 9:00 AM to 11:30 PM (extended hours). This allows trading even after stock market hours. Gold and crude oil markets are most active during evening hours when international markets are open." },
  ];

  const commodities = [
    { name: "Gold", desc: "The ultimate safe-haven asset. Trade gold futures on MCX with expert guidance on price movements driven by global factors, USD strength, and geopolitical events.", color: "from-yellow-500 to-amber-600" },
    { name: "Silver", desc: "Industrial and precious metal with high volatility. Silver offers excellent trading opportunities with strong correlation to gold while being more affordable.", color: "from-gray-400 to-gray-600" },
    { name: "Crude Oil", desc: "World's most traded commodity. Crude oil prices are influenced by OPEC decisions, global demand, and geopolitical tensions. Highly liquid with tight spreads.", color: "from-gray-700 to-gray-900" },
    { name: "Natural Gas", desc: "Energy commodity with seasonal patterns. Natural gas trading offers unique opportunities based on weather, storage data, and production trends.", color: "from-blue-400 to-blue-600" },
    { name: "Copper", desc: "Key industrial metal and economic indicator. Copper demand tracks global industrial activity, making it useful for economic cycle-based trading.", color: "from-orange-500 to-red-600" },
    { name: "Nickel & Zinc", desc: "Essential base metals with growing demand from EV batteries and infrastructure. Emerging opportunities in the clean energy transition.", color: "from-teal-500 to-emerald-600" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.commodityTrading} faqJsonLd={faqSchemas.commodityTrading} />

      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              MCX TRADING
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Commodity Trading
              <span className="block bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                Gold, Silver, Crude & More
              </span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-3xl">
              Diversify your portfolio with commodity trading on MCX. Trade Gold, Silver, Crude Oil, 
              Natural Gas, and industrial metals with research-backed guidance from Rumtek Finvest. 
              As authorized persons of Sunidhi Securities (MCX member), we provide seamless access to 
              commodity markets with expert analysis and risk management.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-orange-600 hover:to-amber-600 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="commodity-cta">
                Start Commodity Trading <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/services" className="border-2 border-orange-400 text-orange-400 hover:bg-orange-500 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-orange-600 mb-1">MCX</div><div className="text-gray-600 text-sm">Exchange Access</div></div>
            <div><div className="text-3xl font-bold text-orange-600 mb-1">Rs.50K</div><div className="text-gray-600 text-sm">Min Margin</div></div>
            <div><div className="text-3xl font-bold text-orange-600 mb-1">9AM-11:30PM</div><div className="text-gray-600 text-sm">Extended Hours</div></div>
            <div><div className="text-3xl font-bold text-orange-600 mb-1">6+</div><div className="text-gray-600 text-sm">Commodities Available</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">Available Commodities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {commodities.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-5`}>
                  {i < 2 ? <Flame className="h-7 w-7 text-white" /> : i < 4 ? <Droplets className="h-7 w-7 text-white" /> : <BarChart3 className="h-7 w-7 text-white" />}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.name}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">Why Trade Commodities with Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              { icon: <Shield className="h-6 w-6 text-orange-600" />, text: "Authorized persons of Sunidhi Securities - MCX member" },
              { icon: <TrendingUp className="h-6 w-6 text-orange-600" />, text: "Daily research reports with buy/sell recommendations" },
              { icon: <CheckCircle className="h-6 w-6 text-orange-600" />, text: "Strict stop-loss discipline for capital protection" },
              { icon: <BarChart3 className="h-6 w-6 text-orange-600" />, text: "Technical and fundamental analysis support" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-gray-50 rounded-xl p-6">
                {item.icon}
                <p className="text-gray-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">Commodity Trading FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-orange-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['equity', 'gold', 'pms', 'mutualFund', 'calculator']} />

      <section className="py-16 bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Diversify with Commodity Trading</h2>
          <p className="text-lg mb-8 text-gray-300">Open your commodity trading account and access MCX markets with expert guidance.</p>
          <Link to="/contact" className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-orange-600 hover:to-amber-600 transition-all inline-flex items-center">
            Get Started <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CommodityTrading;
